import { FC, useEffect, useMemo, useRef, useState } from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import {
    Box,
    Button,
    CircularProgress,
    InputAdornment,
    IconButton,
    Card,
    CardContent,
    Typography,
    Divider,
    Container,
    Link,
} from "@material-ui/core";
import InputField from "src/components/ui-components/InputField";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router";
import { ChangeMobileUserPassword, CheckUserPasswordStatus, SendOTP } from "./ConnectionService";

const initialValues = {
    username: "",
    otp: "",
    password: "",
    confirmPassword: "",
    submit: null,
    AppKey: "",
};

const ChangePassword: FC = (props) => {
    const ref = useRef(null);
    const [canResend, setCanResend] = useState(false);
    const [resendTimer, setResendTimer] = useState(300);
    const { username, appKey } = useParams<{ username?: string; appKey?: string }>();
    const [isOTPSent, setIsOTPSent] = useState(!!username);
    const [isFormVisible, setIsFormVisible] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState("");
    const [showPassword, setShowPassword] = useState({
        otp: false,
        password: false,
        confirmPassword: false,
    });
    const [loadingOTP, setLoadingOTP] = useState(false);
    const isSetPassword = useMemo(() => !!username, [username]);
    const pageTitle = useMemo(() => (isSetPassword ? "Set Password - InvestApp" : "Change Password - InvestApp"), [isSetPassword]);
    const headerText = useMemo(() => (isSetPassword ? "Set Password" : "Change Password"), [isSetPassword]);

    const formatTime = (seconds: number) => {
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        const pad = (num: number) => String(num).padStart(2, "0");
        return `${pad(minutes)}:${pad(secs)}`;
    };

    const handleSavePassword = async (values: { otp: string; password: string }) => {
        const res = await ChangeMobileUserPassword({
            appKey,
            username: (username && username !== '') ? username : ref?.current?.values.username,
            otp: values.otp,
            password: values.password,
            updateReason: isSetPassword ? "SETPASSWORD" : "UPDATEPASSWORD"
        });
        if (res.isSuccess) {
            setSuccessMessage("Password set successfully. You can now log in on the Mobile app");
            setIsOTPSent(false);
        }
    };

    const handleSendOTP = async (usernameinput: string) => {
        setLoadingOTP(true);
        setCanResend(false);
        setResendTimer(300);
        try {
            const res = await SendOTP({ appKey, username: usernameinput, otpReason: "Reset Password" });
            if (res) {
                setIsOTPSent(true);
                if (ref?.current?.values?.otp) {
                    ref.current?.setFieldValue("otp", "");
                    ref.current?.setFieldTouched("otp", false);
                }
            } else {
                setIsOTPSent(false);
            }
        } catch (error) {
            setIsOTPSent(false);
        } finally {
            setLoadingOTP(false);
        }
    };

    const handleKeyUp = (e) => {
        if (e.keyCode === 13) {
            ref.current.handleSubmit();
        }
    };

    useEffect(() => {
        if (username && (!ref?.current?.values?.username || ref?.current?.values?.username === '')) {
            ref.current?.setFieldValue("username", username);
        }
        if (!resendTimer) {
            setCanResend(true);
            return;
        }
        const timer = setInterval(() => setResendTimer((prev) => prev - 1), 1000);
        return () => clearInterval(timer);
    }, [resendTimer, username]);

    useEffect(() => {
        const checkPasswordStatus = async () => {
            try {
                const res = await CheckUserPasswordStatus(username, appKey || "");
                if (res?.statusCode === 200 && res?.status === "true") {
                    if (res?.isSuccess) {
                        setErrorMessage(res.message?? "Password is already set");
                    } else {
                        setIsFormVisible(true);
                    }
                } else if (res?.statusCode === 200 && res?.status === "false") {
                    setErrorMessage(res.message?? "User does not exist.");
                }
                else {
                    setErrorMessage("Something went wrong.");
                }
            } catch (err) {
                Object.entries(err.errors || {}).forEach(([, errMsg]: any) => {
                setErrorMessage( errMsg.errorMessage ?? "Something went wrong.");
                });
            }
        };

        if (username && appKey) {
           checkPasswordStatus();
        }
    }, [username, appKey]);

    // useEffect(() => {
       
    // }, [username, ref?.current]);

    return (
        <>
            <Helmet>
                <title>{pageTitle}</title>
            </Helmet>
            <Box
                sx={{
                    backgroundColor: 'background.default',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '100vh'
                }}
            >
                <Container maxWidth="xs" sx={{ py: '80px' }}>
                    <Card sx={{ overflow: 'visible' }}>
                        <CardContent sx={{ p: 4 }}>
                            <Box>
                                <Typography
                                    color="textPrimary"
                                    gutterBottom
                                    variant="h5"
                                >
                                    {headerText}
                                </Typography>
                            </Box>
                            <Divider sx={{ my: 1 }} />

                            <Box>
                                {(!isSetPassword || isFormVisible) && !successMessage && (
                                    <Formik
                                        innerRef={ref}
                                        initialValues={initialValues}
                                        validationSchema={Yup.object().shape({
                                            username: Yup.string().max(100, "Username cannot exceed 100 characters").required("Username is required"),
                                            otp: Yup.string().max(6).matches(/^[0-9]+$/, 'OTP should only contain numbers').required("OTP is required"),
                                            password: Yup.string()
                                                .required('Password is required')
                                                .min(8, 'Password must be at least 8 characters long')
                                                .max(20, 'Password must not exceed 20 characters')
                                                .matches(/[A-Z]/, 'Password must contain at least one uppercase letter (A-Z)')
                                                .matches(/[a-z]/, 'Password must contain at least one lowercase letter (a-z)')
                                                .matches(/[0-9]/, 'Password must contain at least one number (0-9)')
                                                .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character (e.g., !, @, #, $, etc.)')
                                                .matches(/^\S*$/, 'Password must not contain any spaces')
                                                .test('no-sequences', 'Password must not contain sequences (e.g., 1234, abcd) or repeated characters (e.g., aaa)', value => {
                                                    const hasSequence = /(012|123|234|345|456|567|678|789|890|abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz)/i.test(value);
                                                    const hasRepeated = /(.)\1\1/.test(value);
                                                    return !hasSequence && !hasRepeated;
                                                }),
                                            confirmPassword: Yup.string()
                                                .oneOf([Yup.ref("password"), null], "Password and Confirm Password must match")
                                                .required("Confirm Password is required"),
                                        })}
                                        onSubmit={handleSavePassword}
                                    >
                                        {({ handleSubmit, isSubmitting }): JSX.Element => (
                                            <form noValidate onSubmit={handleSubmit} {...props} autoComplete="off">
                                                <InputField
                                                    name="username"
                                                    label="User Name *"
                                                    // value={(username && username !== '') ? username : ref?.current?.values.username}
                                                    margin="normal"
                                                    autoFocus={!username || username === '' ? true : false}
                                                    // onChange={(e) => {
                                                    //     const newValue = e.target.value;
                                                    //     setFieldValue("username", newValue);
                                                    // }}
                                                    disabled={isOTPSent}
                                                />
                                                {!isOTPSent && !username && (
                                                    <Box sx={{ mt: 2 }}>
                                                        <Button
                                                            disabled={loadingOTP || !ref?.current?.values.username || ref?.current?.values.username === ''}
                                                            onClick={() => handleSendOTP(ref?.current?.values.username)}
                                                            variant="contained"
                                                            size="large"
                                                            fullWidth
                                                        >
                                                            {loadingOTP ? <CircularProgress size={20} /> : "Send OTP"}
                                                        </Button>
                                                    </Box>
                                                )}
                                                {isOTPSent && (
                                                    <>
                                                        <InputField
                                                            name="otp"
                                                            label="OTP *"
                                                            margin="normal"
                                                            autoFocus={!username || username === '' ? false : true}
                                                            type={showPassword.otp ? "text" : "password"}
                                                            InputProps={{
                                                                endAdornment: (
                                                                    <InputAdornment position="end">
                                                                        <IconButton
                                                                            onClick={() => {
                                                                                setShowPassword((prev) => ({ ...prev, otp: !prev.otp }));
                                                                            }}
                                                                        >
                                                                            {showPassword.otp ? <VisibilityOff /> : <Visibility />}
                                                                        </IconButton>
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                        />
                                                        <InputField
                                                            name="password"
                                                            label="Password *"
                                                            margin="normal"
                                                            onKeyUp={handleKeyUp}
                                                            onPaste={(e) => e.preventDefault()}
                                                            type={showPassword.password ? "text" : "password"}
                                                            InputProps={{
                                                                endAdornment: (
                                                                    <InputAdornment position="end">
                                                                        <IconButton
                                                                            onClick={() => {
                                                                                setShowPassword((prev) => ({ ...prev, password: !prev.password }));
                                                                            }}
                                                                        >
                                                                            {showPassword.password ? <VisibilityOff /> : <Visibility />}
                                                                        </IconButton>
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                        />
                                                        <InputField
                                                            name="confirmPassword"
                                                            label="Confirm Password *"
                                                            margin="normal"
                                                            onKeyUp={handleKeyUp}
                                                            onPaste={(e) => e.preventDefault()}
                                                            type={showPassword.confirmPassword ? "text" : "password"}
                                                            InputProps={{
                                                                endAdornment: (
                                                                    <InputAdornment position="end">
                                                                        <IconButton
                                                                            onClick={() => {
                                                                                setShowPassword((prev) => ({ ...prev, confirmPassword: !prev.confirmPassword }));
                                                                            }}
                                                                        >
                                                                            {showPassword.confirmPassword ? <VisibilityOff /> : <Visibility />}
                                                                        </IconButton>
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                        />
                                                        <Box sx={{ mt: 2 }}>
                                                            <Button
                                                                sx={{
                                                                    background: "linear-gradient(to bottom, #072f40, #072f40)",
                                                                }}
                                                                disabled={isSubmitting}
                                                                fullWidth
                                                                size="large"
                                                                type="submit"
                                                                variant="contained"
                                                            >
                                                                Save
                                                                {isSubmitting && (
                                                                    <CircularProgress
                                                                        color="primary"
                                                                        thickness={5}
                                                                        size={20}
                                                                        sx={{ ml: 1 }}
                                                                    />
                                                                )}
                                                            </Button>
                                                        </Box>
                                                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                                                            <Link
                                                                    component="button"
                                                                    onClick={() => handleSendOTP(ref?.current?.values.username)}
                                                                    variant="body2"
                                                                    disabled={!canResend}
                                                                    sx={{
                                                                        color: "textSecondary",
                                                                        textDecoration: canResend ? "underline" : "none",
                                                                        cursor: canResend ? 'pointer' : 'not-allowed',
                                                                        "&:hover": {
                                                                            textDecoration: canResend ? "underline" : "none", // Prevents underline on hover if disabled
                                                                        },
                                                                    }}
                                                                >
                                                                    Resend OTP
                                                                </Link>
                                                            <Typography
                                                                color="textPrimary"
                                                                variant="body2"
                                                            >
                                                                
                                                                {!canResend && <span>&nbsp;in {formatTime(resendTimer)} minutes</span>}
                                                            </Typography>
                                                        </Box>
                                                    </>
                                                )}
                                            </form>
                                        )}
                                    </Formik>
                                )}
                            </Box>
                            {errorMessage && (
                                <Typography color="error" align="center" variant="h6">
                                    {errorMessage}
                                </Typography>
                            )}
                            {successMessage && (
                                <Typography color="success.main" align="center" variant="h6">
                                    {successMessage}
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Container>
            </Box >
        </>
    );
};

export default ChangePassword;